// Mapa de cada par (recurso, action) do antigo Integrations/OktonBank/OB* do
// IFLEX para o endpoint equivalente no Backend próprio (Laravel). Existe para
// que oktonbank.ts continue com a mesma assinatura de sempre
// (chamarOktonBank/enviarOktonBank) e nenhum hook precise mudar - só a
// implementação por baixo troca de destino.
//
// Params sempre viajam como objeto: em GET viram querystring, nos demais
// métodos vão no corpo. Quando a rota do Laravel tem um {id} no caminho, a
// action extrai a chave certa dos params (ela continua indo também no
// corpo/querystring, sem problema: o Laravel prioriza o parâmetro da rota).

export type Metodo = 'get' | 'post' | 'put' | 'delete';

export interface RotaResolvida {
  metodo: Metodo;
  url: string;
}

type Params = Record<string, unknown>;

function valorPreenchido(v: unknown): boolean {
  return v !== undefined && v !== null && v !== '';
}

type Resolvedor = (params: Params) => RotaResolvida;

function rotaFixa(metodo: Metodo, url: string): Resolvedor {
  return () => ({ metodo, url });
}

// Cadastro/edição no mesmo action: sem id (ou vazio) cadastra por POST no
// caminho base; com id, PUT em /caminho/{id} - mesmo padrão em Contas,
// Agências, Cadastros (Moeda/Centro de Custo/Departamento/Direcionador/Plano
// DFC).
function rotaGravar(caminhoBase: string, chaveId = 'id'): Resolvedor {
  return params => {
    const id = params[chaveId];
    return valorPreenchido(id)
      ? { metodo: 'put', url: `${caminhoBase}/${id}` }
      : { metodo: 'post', url: caminhoBase };
  };
}

function rotaComId(
  metodo: Metodo,
  caminhoBase: string,
  chaveId: string,
  sufixo = '',
): Resolvedor {
  return params => ({
    metodo,
    url: `${caminhoBase}/${params[chaveId]}${sufixo}`,
  });
}

const ROTAS: Record<string, Record<string, Resolvedor>> = {
  OBDashboard: {
    listar: rotaFixa('get', '/dashboard'),
    evolucao: rotaFixa('get', '/dashboard/evolucao'),
    contas: rotaFixa('get', '/dashboard/contas'),
    pendencias: rotaFixa('get', '/dashboard/pendencias'),
    movimentacoes: rotaFixa('get', '/dashboard/movimentacoes'),
    ultimasConciliacoes: rotaFixa('get', '/dashboard/ultimas-conciliacoes'),
  },

  OBContas: {
    listar: rotaFixa('get', '/contas-bancarias'),
    gravar: rotaGravar('/contas-bancarias'),
    excluir: rotaComId('delete', '/contas-bancarias', 'conta'),
    listarBancos: rotaFixa('get', '/contas-bancarias/bancos'),
    listarAgencias: rotaFixa('get', '/contas-bancarias/agencias'),
    listarMoedas: rotaFixa('get', '/contas-bancarias/moedas'),
    listarTipos: rotaFixa('get', '/contas-bancarias/tipos'),
    listarVinculos: rotaComId('get', '/contas-bancarias', 'conta', '/vinculos'),
    listarVinculaveis: rotaComId(
      'get',
      '/contas-bancarias',
      'conta',
      '/vinculaveis',
    ),
    gravarVinculos: rotaComId('put', '/contas-bancarias', 'conta', '/vinculos'),
    listarAgenciasCadastro: rotaFixa('get', '/agencias'),
    gravarAgencia: rotaGravar('/agencias'),
    removerAgencia: rotaComId('delete', '/agencias', 'id'),
    listarMunicipios: rotaFixa('get', '/municipios'),
  },

  OBCadastros: {
    listarCentrosCusto: rotaFixa('get', '/cadastros/centros-custo'),
    cadastrarCentroCusto: rotaFixa('post', '/cadastros/centros-custo/atalho'),
    gravarCentroCusto: rotaGravar('/cadastros/centros-custo'),
    removerCentroCusto: rotaComId('delete', '/cadastros/centros-custo', 'id'),

    listarDepartamentos: rotaFixa('get', '/cadastros/departamentos'),
    cadastrarDepartamento: rotaFixa('post', '/cadastros/departamentos/atalho'),
    gravarDepartamento: rotaGravar('/cadastros/departamentos'),
    removerDepartamento: rotaComId('delete', '/cadastros/departamentos', 'id'),

    listarDirecionadores: rotaFixa('get', '/cadastros/direcionadores'),
    cadastrarDirecionador: rotaFixa('post', '/cadastros/direcionadores/atalho'),
    gravarDirecionador: rotaGravar('/cadastros/direcionadores'),
    removerDirecionador: rotaComId('delete', '/cadastros/direcionadores', 'id'),

    buscarClientes: rotaFixa('get', '/cadastros/clientes'),
    cadastrarCliente: rotaFixa('post', '/cadastros/clientes'),
    clientePorDocumento: rotaComId(
      'get',
      '/cadastros/clientes/documento',
      'documento',
    ),
    consultarCnpj: rotaComId('get', '/cadastros/cnpj', 'cnpj'),
    listarPlanosDFC: rotaFixa('get', '/cadastros/planos-dfc'),
    listarPlanosDRE: rotaFixa('get', '/cadastros/planos-dre'),
    parametros: rotaFixa('get', '/cadastros/parametros'),
    listarMoedasPagamento: rotaFixa('get', '/cadastros/moedas-pagamento'),
    listarCaixas: rotaFixa('get', '/cadastros/caixas'),

    listarMoedas: rotaFixa('get', '/cadastros/moedas'),
    gravarMoeda: rotaGravar('/cadastros/moedas'),
    removerMoeda: rotaComId('delete', '/cadastros/moedas', 'id'),

    listarPlanoDFC: rotaFixa('get', '/cadastros/plano-dfc'),
    gravarPlanoDFC: rotaGravar('/cadastros/plano-dfc'),
    sugerirPlanoDFC: rotaFixa('get', '/cadastros/plano-dfc/sugestao'),
    removerPlanoDFC: rotaComId('delete', '/cadastros/plano-dfc', 'id'),
  },

  OBFinanceiro: {
    listar: rotaFixa('get', '/lancamentos-financeiros'),
    resumoMonitor: rotaFixa('get', '/monitor-bancario/resumo'),
    listarAbertos: rotaFixa('get', '/lancamentos-financeiros/abertos'),
    listarMoedasCheque: rotaFixa(
      'get',
      '/lancamentos-financeiros/moedas-cheque',
    ),
    listarChequesDisponiveis: rotaFixa(
      'get',
      '/lancamentos-financeiros/cheques-disponiveis',
    ),
    cadastrar: rotaFixa('post', '/lancamentos-financeiros'),
    excluir: rotaComId('delete', '/lancamentos-financeiros', 'financeiro'),
  },

  OBConciliacao: {
    listar: rotaFixa('get', '/conciliacoes'),
    vincular: rotaFixa('post', '/conciliacoes'),
    baixarEConciliar: rotaFixa('post', '/conciliacoes/baixar-e-conciliar'),
    vincularVarios: rotaFixa('post', '/conciliacoes/vincular-varios'),
    desvincular: rotaComId('delete', '/conciliacoes', 'conciliacao'),
    automatico: rotaFixa('post', '/conciliacoes/automatico'),
    gerarFinanceiro: rotaFixa('post', '/conciliacoes/gerar-financeiro'),
    vincularTransferencia: rotaFixa('post', '/conciliacoes/transferencia'),
    vincularDocumento: rotaFixa('post', '/conciliacoes/documento'),
  },

  OBTransferencia: {
    listar: rotaFixa('get', '/transferencias'),
    cadastrar: rotaFixa('post', '/transferencias'),
    excluir: rotaComId('delete', '/transferencias', 'transferencia'),
  },

  OBSaque: {
    listar: rotaFixa('get', '/saques'),
    cadastrar: rotaFixa('post', '/saques'),
    excluir: rotaComId('delete', '/saques', 'saque'),
  },

  OBDeposito: {
    listar: rotaFixa('get', '/depositos'),
    cadastrar: rotaFixa('post', '/depositos'),
    compensar: rotaComId('put', '/depositos', 'deposito', '/compensar'),
    descompensar: rotaComId('put', '/depositos', 'deposito', '/descompensar'),
    excluir: rotaComId('delete', '/depositos', 'deposito'),
  },

  OBExtrato: {
    listar: rotaFixa('get', '/lancamento-extrato'),
    cadastrar: rotaFixa('post', '/lancamento-extrato'),
    excluir: rotaComId('delete', '/lancamento-extrato', 'extrato'),
    importar: rotaFixa('post', '/lancamento-extrato/import'),
    consultarSicoob: rotaFixa('post', '/lancamento-extrato/sicoob'),
    consultarBradesco: rotaFixa('post', '/lancamento-extrato/bradesco'),
    consultarSantander: rotaFixa('post', '/lancamento-extrato/santander'),
  },
};

export function resolverRota(
  recurso: string,
  action: string,
  params: Params,
): RotaResolvida {
  const resolvedor = ROTAS[recurso]?.[action];
  if (!resolvedor) {
    throw new Error(
      `Rota nao mapeada para o Backend do OktonBank: ${recurso}/${action}.`,
    );
  }
  return resolvedor(params);
}
